import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { ReactNode } from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    formatNumber,
    parseNumber
} from '../../service/FormatHelpers/FormatHelpers';
import { validatePhraseBid } from '../../service/PhraseMoneyCell/PhraseMoneyCell';
import { getTrafaretsAuctionDataByParamValue } from '../../service/PhrasesHelpers/PhrasesHelpers';
import { ThemePopup } from '../../typings/common';
import { CurrencyLimitData } from '../../typings/constants';
import { TrafficVolumeDataItem } from '../../typings/domain';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ErrorItemText } from '../ErrorsPresenter/ErrorsPresenter';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';
import { TrafaretPhrasePriceEditor } from '../TrafaretPhrasePriceEditor/TrafaretPhrasePriceEditor';

import './TrafaretPhrasePriceEditorPopup.css';
import * as keyset from './TrafaretPhrasePriceEditorPopup.i18n';

const i18n = i18nFactory(keyset);

export interface TrafaretPhrasePriceEditorPopupState {
    bid: string;
    trafficVolume?: number;
    amnestyPrice?: number;
}

export interface TrafaretPhrasePriceEditorPopupSaveValue {
    bid: number;
    trafficVolume?: number;
    amnestyPrice?: number;
}

export interface TrafaretPhrasePriceEditorPopupProps {
    initialBid: number;
    trafficVolumeData: Array<TrafficVolumeDataItem>;
    currency: string;
    anchor: ReactNode;
    isPopupOpen: boolean;
    isSaving: boolean;
    onSave: (value: TrafaretPhrasePriceEditorPopupSaveValue) => void;
    onCancel: () => void;
    error?: string;
    priceLimit: CurrencyLimitData;
}

export class TrafaretPhrasePriceEditorPopup extends React.Component<
    TrafaretPhrasePriceEditorPopupProps,
    TrafaretPhrasePriceEditorPopupState
> {
    constructor(props: TrafaretPhrasePriceEditorPopupProps) {
        super(props);

        const { initialBid, trafficVolumeData } = this.props;

        const trafaretsAuctionData = getTrafaretsAuctionDataByParamValue(
            'bid',
            initialBid,
            trafficVolumeData
        );

        if (trafaretsAuctionData) {
            this.state = {
                bid: formatNumber(trafaretsAuctionData.bid),
                trafficVolume: trafaretsAuctionData.trafficVolume,
                amnestyPrice: trafaretsAuctionData.amnestyPrice
            };
        } else {
            this.state = {
                bid: formatNumber(initialBid, 2)
            };
        }
    }

    render() {
        const {
            anchor,
            isPopupOpen,
            currency,
            trafficVolumeData,
            onCancel
        } = this.props;
        const { amnestyPrice, bid, trafficVolume } = this.state;

        return (
            <PopupDecorator
                theme={ThemePopup.CLEAR}
                onOutsideClick={onCancel}
                visible={isPopupOpen}
                hasTail={true}
                directions={[
                    'bottom-center',
                    'top-center',
                    'left-center',
                    'right-center'
                ]}
                target={'anchor'}
                anchor={anchor}
                footer={this.getFooterContent()}
            >
                <TrafaretPhrasePriceEditor
                    bid={bid}
                    trafficVolume={trafficVolume}
                    amnestyPrice={amnestyPrice}
                    onBidChange={this.onBidChange}
                    onBidBlur={this.onBidBlur}
                    onTrafficVolumeChange={this.onTrafficVolumeChange}
                    trafficVolumeMinValue={trafficVolumeData[0].trafficVolume}
                    trafficVolumeMaxValue={
                        trafficVolumeData[trafficVolumeData.length - 1]
                            .trafficVolume
                    }
                    error={this.getError()}
                    currency={currency}
                    adjustmentsRateWarning={i18n('adjustments-rate')}
                />
            </PopupDecorator>
        );
    }

    private onTrafficVolumeChange = (value: number) => {
        const { trafficVolumeData } = this.props;
        const trafaretsAuctionData = getTrafaretsAuctionDataByParamValue(
            'trafficVolume',
            value,
            trafficVolumeData
        );

        this.onTrafaretDataValueChange(trafaretsAuctionData);
    };

    private onBidChange = (value: string) => {
        const { trafficVolumeData } = this.props;

        const bidValue = parseNumber(value);

        const trafaretsAuctionData = getTrafaretsAuctionDataByParamValue(
            'bid',
            bidValue,
            trafficVolumeData
        );

        this.setState({
            bid: value,
            trafficVolume: trafaretsAuctionData
                ? trafaretsAuctionData.trafficVolume
                : undefined,
            amnestyPrice: trafaretsAuctionData
                ? trafaretsAuctionData.amnestyPrice
                : undefined
        });
    };

    private onBidBlur = () => {
        this.setState({
            bid: formatNumber(parseNumber(this.state.bid), 2)
        });
    };

    private onTrafaretDataValueChange = (value?: TrafficVolumeDataItem) => {
        if (value) {
            this.setState({
                bid: formatNumber(value.bid, 2),
                trafficVolume: value.trafficVolume,
                amnestyPrice: value.amnestyPrice
            });
        } else {
            this.setState({
                trafficVolume: undefined,
                amnestyPrice: undefined
            });
        }
    };

    private getFooterContent = () => {
        const { isSaving, onCancel } = this.props;

        return (
            <>
                <ButtonDecorator
                    theme={'action'}
                    size={'n'}
                    view="default"
                    tone="default"
                    text={i18n('button-submit')}
                    disabled={Boolean(this.getError())}
                    onClick={this.onSave}
                    progress={isSaving}
                />
                <ButtonDecorator
                    theme={'pseudo'}
                    size={'n'}
                    view="default"
                    tone="default"
                    text={i18n('button-cancel')}
                    onClick={onCancel}
                    cls={Bem.cls(
                        'trafaret-price-editor-popup',
                        'button-cancel'
                    )}
                />
            </>
        );
    };

    private getError = (): ErrorItemText | undefined => {
        const { error: serverError, priceLimit, currency } = this.props;
        const { bid } = this.state;

        if (serverError) {
            return serverError;
        }

        const clientValidationResult = validatePhraseBid(
            bid,
            priceLimit,
            currency
        );

        if (clientValidationResult.errors) {
            return clientValidationResult.errors[0].text;
        }

        return undefined;
    };

    private onSave = () => {
        const { onSave, onCancel, initialBid } = this.props;
        const { bid, amnestyPrice, trafficVolume } = this.state;
        const bidValue = parseNumber(bid);

        if (initialBid === bidValue) {
            onCancel();

            return;
        }

        if (this.getError()) {
            return;
        }

        onSave({ bid: bidValue, trafficVolume, amnestyPrice });
    };
}
